import React, { useState, useEffect, useRef } from 'react';
import api from '@/api';
import { FormField } from './FormField';
import { Loader2, Save, Send, Palette, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function SettingsToggle({ label, name, settings, setSettings }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-[var(--lux-text-sec)] text-sm">{label}</span>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          className="sr-only"
          checked={settings[name] === 'true' || settings[name] === true}
          onChange={e => setSettings(p => ({ ...p, [name]: e.target.checked ? 'true' : 'false' }))}
          data-testid={`toggle-${name}`}
        />
        <div className={`w-9 h-5 rounded-full border transition-colors ${settings[name] === 'true' || settings[name] === true ? 'bg-[#c9a84c] border-[#c9a84c]' : 'bg-transparent border-[var(--lux-border-4)]'}`}>
          <div className={`w-3 h-3 rounded-full bg-white mt-1 transition-all ${settings[name] === 'true' ? 'ml-5' : 'ml-1'}`} />
        </div>
      </label>
    </div>
  );
}

const TABS = [
  { id: 'general', label: 'Generale' },
  { id: 'smtp', label: 'SMTP' },
  { id: 'emails', label: 'Email' },
  { id: 'template', label: 'Template Email' },
];

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [tab, setTab] = useState('general');
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    api.get('/settings').then(r => { setSettings(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings', settings);
      toast.success('Impostazioni salvate');
      setPreviewKey(k => k + 1);
    } catch { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error('Inserire un indirizzo email'); return; }
    setTesting(true);
    try {
      const res = await api.post('/settings/test-smtp', { to_email: testEmail });
      if (res.data.success) toast.success('Email di test inviata!');
      else toast.error(res.data.message || 'Invio fallito');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore test SMTP');
    } finally { setTesting(false); }
  };

  const previewUrl = () => {
    const params = new URLSearchParams({
      primary_color: settings.email_tmpl_primary_color || '#c9a84c',
      logo_url: settings.email_tmpl_logo_url || '',
      header_subtitle: settings.email_tmpl_header_subtitle || '',
      footer_text: settings.email_tmpl_footer_text || '',
      property_name: settings.app_name || 'Hotel Esempio',
    });
    return `${API_URL}/api/settings/email-template-preview?${params}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-gold">Sistema</p>
          <h2 className="font-serif text-3xl text-white font-light mt-1">Impostazioni</h2>
        </div>
        <button onClick={save} disabled={saving} className="btn-gold flex items-center gap-2" data-testid="save-settings-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? 'Salvataggio...' : 'Salva'}
        </button>
      </div>

      <div className="flex gap-1 border-b border-[var(--lux-border-1)]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2.5 text-xs uppercase tracking-widest transition-colors
              ${tab === t.id ? 'border-b-2 border-[#c9a84c] text-[#c9a84c]' : 'text-[var(--lux-text-muted-2)] hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className={tab === 'template' ? '' : 'max-w-2xl'}>
        {tab === 'general' && (
          <div className="card-dark rounded-lg p-6 space-y-4">
            <FormField label="Nome App" name="app_name" placeholder="Shangrilux" form={settings} setForm={setSettings} testId="settings-app_name" />
            <FormField label="Colore Accento" name="primary_color" placeholder="#c9a84c" form={settings} setForm={setSettings} testId="settings-primary_color" />
            <FormField label="Email Notifiche Admin" name="notify_email" type="email" placeholder="admin@villa.it" form={settings} setForm={setSettings} testId="settings-notify_email" />
          </div>
        )}
        {tab === 'smtp' && (
          <div className="space-y-4">
            <div className="card-dark rounded-lg p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Host SMTP" name="smtp_host" placeholder="smtp.gmail.com" form={settings} setForm={setSettings} testId="settings-smtp_host" />
                <FormField label="Porta" name="smtp_port" placeholder="587" form={settings} setForm={setSettings} testId="settings-smtp_port" />
              </div>
              <FormField label="Username SMTP" name="smtp_user" placeholder="user@email.com" form={settings} setForm={setSettings} testId="settings-smtp_user" />
              <FormField label="Password SMTP" name="smtp_pass" type="password" placeholder="••••••••" form={settings} setForm={setSettings} testId="settings-smtp_pass" />
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Nome mittente" name="smtp_from_name" placeholder="Shangrilux" form={settings} setForm={setSettings} testId="settings-smtp_from_name" />
                <FormField label="Email mittente" name="smtp_from_email" type="email" placeholder="noreply@villa.it" form={settings} setForm={setSettings} testId="settings-smtp_from_email" />
              </div>
              <div>
                <label className="label-muted block mb-1">Sicurezza</label>
                <select className="input-dark" value={settings.smtp_secure || 'tls'}
                  onChange={e => setSettings(p => ({ ...p, smtp_secure: e.target.value }))} data-testid="settings-smtp_secure">
                  <option value="tls">TLS (STARTTLS — porta 587)</option>
                  <option value="ssl">SSL (porta 465)</option>
                  <option value="none">Nessuna (porta 25)</option>
                </select>
              </div>
            </div>
            <div className="card-dark rounded-lg p-5">
              <p className="label-gold text-xs mb-4">Invia Email di Test</p>
              <div className="flex gap-3">
                <input className="input-dark flex-1" type="email" placeholder="test@email.com"
                  value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="test-email-input" />
                <button onClick={sendTest} disabled={testing} className="btn-gold flex items-center gap-2 whitespace-nowrap" data-testid="send-test-email-btn">
                  {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Test
                </button>
              </div>
            </div>
          </div>
        )}
        {tab === 'emails' && (
          <div className="card-dark rounded-lg p-6 space-y-1">
            <p className="text-[var(--lux-text-muted-3)] text-xs mb-4">Attiva/disattiva i singoli tipi di email automatiche.</p>
            <SettingsToggle label="Notifica admin nuova richiesta" name="email_admin_notify_enabled" settings={settings} setSettings={setSettings} />
            <SettingsToggle label="Email preventivo all'ospite" name="email_quote_enabled" settings={settings} setSettings={setSettings} />
            <SettingsToggle label="Email conferma prenotazione" name="email_confirm_enabled" settings={settings} setSettings={setSettings} />
            <SettingsToggle label="Email rifiuto richiesta" name="email_reject_enabled" settings={settings} setSettings={setSettings} />
            <SettingsToggle label="Email revoca chiave" name="email_revoke_enabled" settings={settings} setSettings={setSettings} />
          </div>
        )}
        {tab === 'template' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Editor */}
            <div className="card-dark rounded-lg p-6 space-y-5">
              <div className="flex items-center gap-2">
                <Palette size={15} style={{ color: '#c9a84c' }} />
                <h3 className="text-sm font-semibold" style={{ color: '#e5e5e5' }}>Personalizzazione Template</h3>
              </div>

              {/* Color picker */}
              <div>
                <label className="label-muted block mb-2">Colore primario email</label>
                <div className="flex gap-3 items-center">
                  <input type="color"
                    value={settings.email_tmpl_primary_color || '#c9a84c'}
                    onChange={e => setSettings(p => ({ ...p, email_tmpl_primary_color: e.target.value }))}
                    data-testid="email-tmpl-color"
                    className="w-10 h-10 rounded cursor-pointer border-0"
                    style={{ padding: 0, background: 'transparent' }} />
                  <input className="input-dark flex-1 font-mono text-sm"
                    value={settings.email_tmpl_primary_color || '#c9a84c'}
                    onChange={e => setSettings(p => ({ ...p, email_tmpl_primary_color: e.target.value }))}
                    placeholder="#c9a84c" data-testid="email-tmpl-color-hex" />
                </div>
                <p className="text-[11px] mt-1.5" style={{ color: 'rgba(229,229,229,0.3)' }}>
                  Colore usato per intestazione, bottone CTA e link.
                </p>
              </div>

              {/* Logo */}
              <div>
                <label className="label-muted block mb-1">URL Logo (opzionale)</label>
                <input className="input-dark"
                  placeholder="https://tuaproprietà.it/logo.png"
                  value={settings.email_tmpl_logo_url || ''}
                  onChange={e => setSettings(p => ({ ...p, email_tmpl_logo_url: e.target.value }))}
                  data-testid="email-tmpl-logo-url" />
                <p className="text-[11px] mt-1.5" style={{ color: 'rgba(229,229,229,0.3)' }}>
                  Se vuoto, viene mostrato il nome della proprietà come testo.
                </p>
              </div>

              {/* Subtitle */}
              <div>
                <label className="label-muted block mb-1">Sottotitolo intestazione</label>
                <input className="input-dark"
                  placeholder="Comunicazione automatica"
                  value={settings.email_tmpl_header_subtitle || ''}
                  onChange={e => setSettings(p => ({ ...p, email_tmpl_header_subtitle: e.target.value }))}
                  data-testid="email-tmpl-subtitle" />
              </div>

              {/* Footer */}
              <div>
                <label className="label-muted block mb-1">Testo footer</label>
                <input className="input-dark"
                  placeholder="Comunicazione automatica, non rispondere a questa email."
                  value={settings.email_tmpl_footer_text || ''}
                  onChange={e => setSettings(p => ({ ...p, email_tmpl_footer_text: e.target.value }))}
                  data-testid="email-tmpl-footer" />
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={save} disabled={saving} className="btn-gold flex items-center gap-2 flex-1" data-testid="save-email-template-btn">
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Salva Template
                </button>
                <button onClick={() => setPreviewKey(k => k + 1)}
                  className="flex items-center gap-2 px-4 py-2 text-sm rounded-sm"
                  style={{ border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c' }}
                  data-testid="refresh-preview-btn">
                  <RefreshCw size={13} /> Aggiorna
                </button>
              </div>
            </div>

            {/* Live Preview */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Eye size={14} style={{ color: '#c9a84c' }} />
                <h3 className="text-sm font-semibold" style={{ color: '#e5e5e5' }}>Anteprima Email</h3>
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(201,168,76,0.1)', color: '#c9a84c' }}>
                  Live
                </span>
              </div>
              <div className="rounded-sm overflow-hidden" style={{ border: '1px solid #222', height: '520px' }}>
                <iframe
                  key={previewKey}
                  src={previewUrl()}
                  title="Anteprima Template Email"
                  data-testid="email-preview-iframe"
                  className="w-full h-full border-0"
                  style={{ background: '#fff' }}
                />
              </div>
              <p className="text-[10px]" style={{ color: 'rgba(229,229,229,0.3)' }}>
                Anteprima con dati di esempio. Clicca &quot;Salva&quot; o &quot;Aggiorna anteprima&quot; per aggiornare.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
